// src/pages/AdminUsersPage.js

import React, { useEffect, useState } from 'react'
import {
  collection,
  query,
  onSnapshot,
  updateDoc,
  doc,
  addDoc,
  where,
  getDocs,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../firebase'
import {
  Container,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Box,
  Button
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PeopleAltIcon from '@mui/icons-material/PeopleAlt'
import MailIcon from '@mui/icons-material/MailOutline'
import { useAuth } from '../contexts/AuthContext'

// Çoklu dil
import { useTranslation } from 'react-i18next'

// TabPanel bileşeni: Her sekmede gösterilecek içeriği sarmak için
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  )
}

export default function AdminUsersPage() {
  const { t } = useTranslation()
  
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })
  const [searchTerm, setSearchTerm] = useState('') // Arama metni

  // Sekme durumu
  const [tabValue, setTabValue] = useState(0)

  // Admin kontrolü
  const [isAdmin, setIsAdmin] = useState(false)

  // Auth
  const { currentUser, userRole, organization } = useAuth()

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteSending, setInviteSending] = useState(false)

  // Firestore'dan kullanıcıları çek
  useEffect(() => {
    if (!organization?.id) {
      setUsers([])
      setLoading(false)
      return
    }

    const usersRef = collection(db, 'users')
    const q = query(usersRef, where('organizationId', '==', organization.id))
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const userList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))

      const onlyVerifiedUsers = userList.filter((u) => u.emailVerified === true)
      setUsers(onlyVerifiedUsers)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [organization?.id])

  useEffect(() => {
    // Admin kontrolü
    if (userRole === 'admin') {
      setIsAdmin(true)
    } else {
      setIsAdmin(false)
    }
  }, [currentUser, userRole])

  // Mevcut admin sayısını hesaplamak için yardımcı fonksiyon
  const getAdminCount = () => {
    return users.filter((u) => u.role === 'admin').length
  }

  // Rol güncelle
  const handleRoleChange = async (userId, newRole) => {
    try {
      const userToChange = users.find((u) => u.id === userId)
      if (!userToChange) return

      const adminCount = getAdminCount()

      if (
        adminCount === 1 &&
        userToChange.role === 'admin' &&
        newRole === 'user'
      ) {
        setSnackbar({
          open: true,
          message: t('adminUsers.adminWarning'),
          severity: 'error',
        })
        return
      }

      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, { role: newRole })

      setSnackbar({
        open: true,
        message: t('adminUsers.userRoleUpdated'),
        severity: 'success',
      })
    } catch (error) {
      console.error('Rol güncelleme hatası:', error)
      setSnackbar({
        open: true,
        message: t('adminUsers.userRoleError'),
        severity: 'error',
      })
    }
  }

  // Durum güncelle
  const handleStatusChange = async (userId, newStatus) => {
    try {
      const userRef = doc(db, 'users', userId)
      await updateDoc(userRef, { status: newStatus })

      setSnackbar({
        open: true,
        message: t('adminUsers.statusUpdated'),
        severity: 'success',
      })
    } catch (error) {
      console.error('Durum güncelleme hatası:', error)
      setSnackbar({
        open: true,
        message: t('adminUsers.statusError'),
        severity: 'error',
      })
    }
  }

  const handleSendInvite = async () => {
    const emailTrimmed = inviteEmail.trim()
    const emailLower = emailTrimmed.toLowerCase()

    if (!emailTrimmed) {
      setSnackbar({
        open: true,
        message: t('adminUsers.inviteEmailRequired'),
        severity: 'warning'
      })
      return
    }

    if (!organization?.id) {
      setSnackbar({
        open: true,
        message: t('adminUsers.organizationMissing'),
        severity: 'error'
      })
      return
    }

    if (users.some((u) => (u.email || '').toLowerCase() === emailLower)) {
      setSnackbar({
        open: true,
        message: t('adminUsers.userAlreadyInOrg'),
        severity: 'info'
      })
      return
    }

    setInviteSending(true)
    try {
      const existingInviteSnap = await getDocs(
        query(
          collection(db, 'invites'),
          where('emailLower', '==', emailLower),
          where('organizationId', '==', organization.id),
          where('status', '==', 'pending')
        )
      )

      if (!existingInviteSnap.empty) {
        setSnackbar({
          open: true,
          message: t('adminUsers.inviteAlreadySent'),
          severity: 'info'
        })
        setInviteSending(false)
        return
      }

      await addDoc(collection(db, 'invites'), {
        email: emailTrimmed,
        emailLower,
        organizationId: organization.id,
        status: 'pending',
        createdAt: serverTimestamp(),
        createdBy: currentUser?.uid || null
      })

      setSnackbar({
        open: true,
        message: t('adminUsers.inviteSent'),
        severity: 'success'
      })
      setInviteEmail('')
    } catch (error) {
      console.error('Invite send error:', error)
      setSnackbar({
        open: true,
        message: t('adminUsers.inviteError'),
        severity: 'error'
      })
    } finally {
      setInviteSending(false)
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  // Kullanıcı arama
  const filteredUsers = users.filter((user) => {
    const text = searchTerm.toLowerCase()
    const emailMatch = user.email?.toLowerCase().includes(text)
    const firstNameMatch = user.firstName?.toLowerCase().includes(text)
    const lastNameMatch = user.lastName?.toLowerCase().includes(text)
    return emailMatch || firstNameMatch || lastNameMatch
  })

  // Aktif ve pasif kullanıcıları ayır
  const activeUsers = filteredUsers.filter((user) => user.status === 'active')
  const passiveUsers = filteredUsers.filter((user) => user.status === 'passive')

  // Sekme değişimi
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>{t('adminUsers.loading')}</Typography>
      </Container>
    )
  }

  if (!organization?.id) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">{t('adminUsers.organizationMissing')}</Alert>
      </Container>
    )
  }

  if (!isAdmin) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{t('adminUsers.noAccess')}</Alert>
      </Container>
    )
  }

  return (
    <Container sx={{ mt: 4, backgroundColor: '#f9f9f9', padding: 4, borderRadius: 2 }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ color: '#3f51b5' }}>
        <PeopleAltIcon sx={{ mr: 1 }} />
        {t('adminUsers.pageTitle')}
      </Typography>

      {organization?.name && (
        <Typography align="center" sx={{ mb: 2, color: 'text.secondary' }}>
          {t('adminUsers.organizationLabel')}: {organization.name}
        </Typography>
      )}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardHeader title={t('adminUsers.inviteTitle')} />
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8}>
              <TextField
                label={t('adminUsers.inviteEmailLabel')}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                variant="outlined"
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSendInvite}
                disabled={inviteSending}
              >
                {inviteSending ? t('adminUsers.inviteSending') : t('adminUsers.inviteSendButton')}
              </Button>
            </Grid>
          </Grid>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {t('adminUsers.inviteHint')}
          </Typography>
        </CardContent>
      </Card>

      {/* Arama alanı */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t('adminUsers.searchLabel')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {/* Sekmeler (Active - Passive) */}
      <Tabs value={tabValue} onChange={handleTabChange} centered>
        <Tab label={t('adminUsers.activeTab')} />
        <Tab label={t('adminUsers.passiveTab')} />
      </Tabs>

      {/* Active Kullanıcılar TabPanel */}
      <TabPanel value={tabValue} index={0}>
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardHeader title={t('adminUsers.activeTab')} />
          <CardContent>
            <List>
              {activeUsers.map((user) => (
                <ListItem
                  key={user.id}
                  sx={{
                    mb: 1,
                    backgroundColor: '#f1f1f1',
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    p: 1, 
                  }}
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatarUrl || ''}>
                      {`${user.firstName || ''} ${user.lastName || ''}`.trim().charAt(0) ||
                        user.email?.charAt(0) ||
                        '?'}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                    secondary={user.email}
                    sx={{ flex: 1, mb: { xs: 1, sm: 0 } }}
                  />

                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2
                    }}
                  >
                    <FormControl
                      size="small"
                      sx={{ minWidth: 120, width: 110 }}
                    >
                      <InputLabel>{t('adminUsers.roleLabel')}</InputLabel>
                      <Select
                        label={t('adminUsers.roleLabel')}
                        value={user.role || t('adminUsers.user')}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                      >
                        <MenuItem value="user">{t('adminUsers.user')}</MenuItem>
                        <MenuItem value="admin">{t('adminUsers.admin')}</MenuItem>
                      </Select>
                    </FormControl>

                    <FormControl
                      size="small"
                      sx={{ minWidth: 120, width: 120 }}
                    >
                      <InputLabel>{t('adminUsers.statusLabel')}</InputLabel>
                      <Select
                        label={t('adminUsers.statusLabel')}
                        value={user.status || t('adminUsers.active')}
                        onChange={(e) =>
                          handleStatusChange(user.id, e.target.value)
                        }
                      >
                        <MenuItem value="active">{t('adminUsers.active')}</MenuItem>
                        <MenuItem value="passive">{t('adminUsers.passive')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Passive Kullanıcılar TabPanel */}
      <TabPanel value={tabValue} index={1}>
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardHeader title={t('adminUsers.passiveTab')} />
          <CardContent>
            <List>
              {passiveUsers.map((user) => (
                <ListItem
                  key={user.id}
                  sx={{
                    mb: 1,
                    backgroundColor: '#f1f1f1',
                    borderRadius: 1,
                  }}
                  >
                    <ListItemAvatar>
                      <Avatar src={user.avatarUrl || ''}>
                        {`${user.firstName || ''} ${user.lastName || ''}`.trim().charAt(0) ||
                          user.email?.charAt(0) ||
                          '?'}
                      </Avatar>
                    </ListItemAvatar>
                  <ListItemText
                    primary={`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                    secondary={user.email}
                    sx={{ flex: 1 }}
                  />

                  {/* Rol Seçimi */}
                  <FormControl
                    size="small"
                    sx={{ mr: 2, minWidth: 120, width: 110 }}
                  >
                    <InputLabel>{t('adminUsers.roleLabel')}</InputLabel>
                    <Select
                      label={t('adminUsers.roleLabel')}
                      value={user.role || t('adminUsers.user')}
                      onChange={(e) => handleRoleChange(user.id, e.target.value)}
                    >
                      <MenuItem value="user">{t('adminUsers.user')}</MenuItem>
                      <MenuItem value="admin">{t('adminUsers.admin')}</MenuItem>
                    </Select>
                  </FormControl>

                  {/* Kullanıcı Durum Seçimi */}
                  <FormControl
                    size="small"
                    sx={{ mr: 2, minWidth: 120, width: 120 }}
                  >
                    <InputLabel>{t('adminUsers.statusLabel')}</InputLabel>
                    <Select
                      label={t('adminUsers.statusLabel')}
                      value={user.status || t('adminUsers.active')}
                      onChange={(e) =>
                        handleStatusChange(user.id, e.target.value)
                      }
                    >
                      <MenuItem value="active">{t('adminUsers.active')}</MenuItem>
                      <MenuItem value="passive">{t('adminUsers.passive')}</MenuItem>
                    </Select>
                  </FormControl>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
